import { useMemo } from "react";
import { apiSlice } from "./api/apiSlice";
import { filterActiveStaff, filterAssignableStaff } from "../Shared/staffStatus";

const employeeSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    addFavourite: builder.mutation({
      query: (id) => ({
        url: "employee/addfavourite",
        method: "PUT",
        body: id,
      }),
      invalidatesTags: ["Employee", "Leads"],
    }),
    getFavouriteClient: builder.query({
      query: () => ({
        url: "employee/favourite",
      }),
      providesTags: ["Employee"],
    }),
    getLeadsReport: builder.query({
      query: () => ({
        url: "employee/leadsreports",
      }),
      transformResponse: (response) => response.leadReport,
      providesTags: ["Employee"],
    }),

    // New optimized dashboard endpoint
    getDashboardComplete: builder.query({
      query: (args) => {
        const staffId = args?.staffId;
        return {
          url: staffId ? `dashboard/dashboard-complete?employeeId=${staffId}` : "dashboard/dashboard-complete",
        };
      },
      transformResponse: (response) => response.data,
      providesTags: ["Employee"],
    }),

    // Get meeting tasks for modal display
    getMeetingTasks: builder.query({
      query: (args) => {
        const { staffId, status = 'all' } = args || {};
        const queryParams = new URLSearchParams();
        
        if (staffId) queryParams.append("employeeId", staffId);
        if (status && status !== 'all') queryParams.append("status", status);
        
        return {
          url: `dashboard/meeting-tasks?${queryParams.toString()}`,
        };
      },
      transformResponse: (response) => response.data,
      providesTags: ["Employee"],
    }),


    getQuarterlyLeadTask: builder.query({
      query: () => ({
        url: "employee/quarterlytasks",
      }),
      transformResponse: (response) => response.quarterlytasks,
      providesTags: ["Employee"],
    }),
    getTodayPendingTask: builder.query({
      query: () => ({
        url: "/employee/kpi/today-pending-task",
      }),
      transformResponse: (response) => response,
      providesTags: ["Employee"],
    }),
    getCallReports: builder.query({
      query: () => ({
        url: "employee/callreports",
      }),
      transformResponse: (response) => response.callsReports,
      providesTags: ["Employee"],
    }),
    getClassification: builder.query({
      query: () => ({
        url: "/employee/leads/classification",
      }),
      transformResponse: (response) => response.leadReport,
    }),
    getMeetings: builder.query({
      query: () => ({
        url: "/employee/count/meeting",
      }),
      transformResponse: (response) => response,
    }),
    /**
     * Every employee on record, whatever state they are in.
     *
     * Only Manage Staff should call this directly — it is the one screen whose
     * job is to show blocked and removed people. Everywhere else, use
     * `useGetActiveStaffQuery` below.
     */
    getStaff: builder.query({
      query: () => ({
        url: "/employee/all-staff",
      }),
      transformResponse: (response) => response.staff,
      providesTags: ["Employee"],
    }),
    getStaffKpi: builder.query({
      query: ({ staff, start, end, project }) => ({
        url: `/kpi/total-report?user=${JSON.stringify(
          staff
        )}&start=${start}&end=${end}&projectID=${project}`,
      }),
      transformResponse: (response) => response.employeeKPIReports,
    }),
    editStaff: builder.mutation({
      query: ({ staffId, formData }) => ({
        url: `/employee/edit/${staffId}`,
        method: "PUT",
        body: formData,
      }),
      invalidatesTags: ["Employee"],
    }),

    blockStaff: builder.mutation({
      query: ({ staffId }) => ({
        url: `/employee/${staffId}/block`,
        method: "PUT",
      }),
      invalidatesTags: ["Employee"],
    }),
    emailReport: builder.mutation({
      query: ({ data }) => ({
        url: "/kpi/reports-ceo",
        method: "POST",
        body: data,
      }),
    }),
    hierarchyCreate: builder.mutation({
      query: ({ data }) => ({
        url: "/hierarchical/create",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Employee"],
    }),
    hierarchyUpdate: builder.mutation({
      query: ({ data }) => ({
        url: "/hierarchical/update",
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Employee"],
    }),
    getHierarchy: builder.query({
      query: () => ({
        url: "/hierarchical",
      }),
      transformResponse: (response) => response.hierarchical,
      providesTags: ["Employee"],
    }),
    getHierarchysubordinates: builder.query({
      query: (staffId) => ({
        url: `hierarchical/subordinates/${staffId}`,
      }),
      transformResponse: (response) => response.hierarchi,
      providesTags: ["Employee"],
    }),
    /**
     * Your team.
     *
     * `includeBlocked` is off by default and must stay that way: this one query
     * feeds the staff picker on Search Leads, Manage Leads, Targets, Clients,
     * CMP, the KPI activity pages and the dashboards, and none of those show a
     * status badge. Manage Team opts in because it does.
     *
     * The flag is part of the URL, so RTK Query caches the two variants
     * separately and a picker cannot be served the badge-less list's cache.
     */
    getSingleHierarchy: builder.query({
      query: ({ staffId, includeBlocked = false }) => ({
        url: `/hierarchical/${staffId}${
          includeBlocked ? "?includeBlocked=true" : ""
        }`,
      }),
      transformResponse: (response) => response.hierarchical,
      providesTags: ["Employee"],
    }),
    deleteSingleStaff: builder.mutation({
      query: ({ parentId, staffId }) => ({
        url: `/hierarchical/employee/${parentId}/subordinate/${staffId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Employee"],
    }),

    /**
     * What removing this employee would do — read-only, safe to call freely.
     *
     * Returns { employee, impact }. `impact.movable` is the open work that
     * would be handed to the recipients; `impact.frozen` is commission,
     * targets and completed activity that stays with them permanently, and is
     * why most accounts cannot be deleted outright. `canHardDelete` is true
     * only for an account that has never been used.
     *
     * keepUnusedDataFor: 0 because this previews live state immediately before
     * an irreversible action — a cached figure that no longer matches the
     * database is worse than a second of loading.
     */
    getRemovalImpact: builder.query({
      query: (staffId) => ({
        url: `/employee/${staffId}/removal-impact`,
      }),
      keepUnusedDataFor: 0,
      providesTags: ["Employee"],
    }),

    /**
     * Removes an employee. Irreversible — there is no undo.
     *
     * The server decides between deleting the record outright (only when it
     * has no footprint at all) and reassigning the open work then deactivating
     * them. `result.mode` reports which happened.
     */
    removeEmployee: builder.mutation({
      query: ({ staffId, recipients, mode, newManagerId, reason }) => ({
        url: `/employee/${staffId}`,
        method: "DELETE",
        body: { recipients, mode, newManagerId, reason },
      }),
      invalidatesTags: ["Employee", "Leads", "Client"],
    }),
    assignStaffLeads: builder.mutation({
      query: ({ staffId, data }) => ({
        url: `/client/assign-clients/${staffId}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Leads", "Employee"],
    }),
    getTeamClients: builder.query({
      query: ({ teamId }) => ({
        url: `/client/emp/${teamId}`,
      }),
      transformResponse: (response) => response.specEmployeeClients,
      providesTags: ["Employee"],
    }),
    assignSelectedClients: builder.mutation({
      query: ({ data, id }) => ({
        url: `/client/assign-selected-clients/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Leads", "Employee"],
    }),
    assignExelClients: builder.mutation({
      query: ({ data, id }) => ({
        url: `/client/assign-selected-clients/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Leads", "Employee"],
    }),
    logout: builder.mutation({
      query: () => {
        const token = localStorage.getItem("token");
        return {
          url: "auth/logout",
          method: "PUT",
          headers: {
            token: `JWT ${token}`,
          },
        };
      },
      invalidatesTags: ["Employee"],
    }),
  }),
});

/**
 * Active employees only — the default for every picker in the app.
 *
 * Assigning a lead, a client, a target or a hierarchy seat to someone who is
 * blocked or has left the company creates work nobody will ever do, and the
 * only signal the old dropdowns gave was that the name was still in the list.
 * Choosing who does something next is always a question about active people, so
 * that filter belongs in the hook rather than repeated (and forgotten) at
 * nineteen call sites.
 *
 * This deliberately reuses the `getStaff` cache entry rather than asking the
 * server for a filtered list: one request serves the whole app, tab switches on
 * Manage Staff are instant, and there is no second copy of the roster that can
 * drift. The useMemo matters — without it the filtered array is a new reference
 * on every render and every consumer re-renders on any unrelated store change.
 *
 * Same shape as `useGetStaffQuery`, so swapping one for the other is a
 * one-word change at the call site.
 */
export const useGetActiveStaffQuery = (arg, options) => {
  const result = employeeSlice.useGetStaffQuery(arg, options);
  const data = useMemo(() => filterActiveStaff(result.data), [result.data]);
  return { ...result, data };
};

/**
 * Active AND suspended staff, on the same shared `getStaff` cache entry.
 *
 * The picker counterpart to Manage Team's list. Only reach for this on a screen
 * that shows the person's status next to their name — see filterAssignableStaff
 * for why. If you are about to use it anywhere else, you almost certainly want
 * useGetActiveStaffQuery.
 */
export const useGetAssignableStaffQuery = (arg, options) => {
  const result = employeeSlice.useGetStaffQuery(arg, options);
  const data = useMemo(() => filterAssignableStaff(result.data), [result.data]);
  return { ...result, data };
};

export const {
  useGetDashboardCompleteQuery,
  useGetMeetingTasksQuery,
  useAddFavouriteMutation,
  useGetFavouriteClientQuery,
  useGetLeadsReportQuery,
  useGetQuarterlyLeadTaskQuery,
  useGetTodayPendingTaskQuery,
  useGetCallReportsQuery,
  useGetClassificationQuery,
  useGetMeetingsQuery,
  useGetStaffQuery,
  useLazyGetStaffKpiQuery,
  useEditStaffMutation,
  useBlockStaffMutation,
  useEmailReportMutation,
  useHierarchyCreateMutation,
  useHierarchyUpdateMutation,
  useGetHierarchyQuery,
  useGetHierarchysubordinatesQuery,
  useGetSingleHierarchyQuery,
  useAssignStaffLeadsMutation,
  useGetTeamClientsQuery,
  useAssignSelectedClientsMutation,
  useDeleteSingleStaffMutation,
  useGetRemovalImpactQuery,
  useRemoveEmployeeMutation,
  useAssignExelClientsMutation,
  useLogoutMutation
} = employeeSlice;
